import React, { useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import FormGroup from "app/components/common/inputs/FormGroup";
import RadioButton from "app/components/common/buttons/RadioButton";
import SelectField from "app/components/common/inputs/SelectField";
import CustomDatePicker from "app/components/common/inputs/CustomDatePicker";
import InputField from "app/components/common/inputs/InputField";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { JointemploymentDetailsFromThree } from "app/utils/types";
import { stateOfIssueOptions } from "app/utils/staticValues";

const DisabledPreviousEmployerDetails: React.FC = () => {
  const {
    control,
    formState: { errors },
    setValue,
    setError,
    register,
    clearErrors,
    getValues,
  } = useForm<JointemploymentDetailsFromThree>();
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >("input, select");

      elements.forEach((el) => {
        el.disabled = true;
        el.tabIndex = -1;
      });
    }
  }, []);
  return (
    <>
      <div
        className="w-full  bg-white opacity-25 cursor-not-allowed"
        ref={containerRef}
      >
        <FormGroup>
          <InputField
            label="Job title"
            name="yourDetails.jobTitle"
            control={control}
            type="text"
            required={true}
            placeholder="Designer"
            error={errors.yourDetails?.jobTitle?.message}
            className="flex-1"
            rules={{
              required: "Job title is required",
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Only alphabets are allowed",
              },
            }}
          />
          <InputField
            label="Previous employer name"
            name="yourDetails.employerName"
            control={control}
            type="text"
            required={true}
            placeholder="Katrina"
            error={errors?.yourDetails?.employerName?.message}
            className="flex-1"
            rules={{
              required: "employer name is required",
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Only alphabets are allowed",
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <CustomDatePicker
            name="yourDetails.startDate"
            control={control}
            label="Start Date"
            placeholder="DD/MM/YYYY"
            rules={{ required: "Date is required" }}
            className="flex-1"
            isPastDate={true}
          />
          <CustomDatePicker
            name="yourDetails.endDate"
            control={control}
            label="End date"
            placeholder="DD/MM/YYYY"
            className="flex-1"
            isPastDate={true}
            rules={{
              required: "Date is required",
              validate: (value: Date | string | null) => {
                const startDate = getValues("yourDetails.startDate");
                if (!startDate || !value) return true;
                const start = new Date(startDate);
                const end = new Date(value);
                if (end < start) {
                  return "End date cannot be before start date";
                }

                return true;
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <AutocompleteLocations
            FieldName="yourDetails.street"
            placeholder="1901 Thornridge Cir. Shiloh,"
            register={register}
            setValue={setValue}
            setError={setError}
            label="Employer address"
            required={true}
            className="flex-1"
            error={errors?.yourDetails?.street?.message}
            stateFieldName="yourDetails.state"
            postCodeFieldName="yourDetails.postCode"
            clearErrors={clearErrors}
          />
          <SelectField
            label="state"
            name="yourDetails.state"
            control={control}
            options={stateOfIssueOptions}
            placeholder="Select state"
            error={errors?.yourDetails?.state?.message}
            required={true}
            className="flex-1"
          />
        </FormGroup>
        <FormGroup>
          <InputField
            label="Post Code"
            name="yourDetails.postCode"
            control={control}
            type="number"
            required={true}
            placeholder="8767"
            error={errors?.yourDetails?.postCode?.message}
            className="flex-1"
            rules={{
              min: {
                value: 1,
                message: "value must be greater than 0",
              },
            }}
          />
          <div className="flex-1">
            <div className="text-left mb-5">
              <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                Do you have a second job?
              </h6>
              <div className="flex gap-5">
                <Controller
                  name="yourDetails.secondjob"
                  control={control}
                  rules={{ required: "Please select an option" }}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex gap-5">
                      <RadioButton
                        id="yes"
                        name="yourDetails.secondjob"
                        value="yes"
                        label="Yes"
                        checked={value === "yes"}
                        onChange={onChange}
                      />
                      <RadioButton
                        id="no"
                        name="yourDetails.secondjob"
                        value="no"
                        label="No"
                        checked={value === "no"}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
              </div>
              {errors?.yourDetails?.secondjob && (
                <ErrorsMessage
                  title={errors?.yourDetails?.secondjob.message}
                  className="!text-left"
                />
              )}
            </div>
          </div>
        </FormGroup>
      </div>
    </>
  );
};

export default DisabledPreviousEmployerDetails;
